import { useCallback, useState } from "react";
import { practiceDisclosure } from "../../practice/disclosure.ts";
import { QUALITY_BANDS, type QualityBandId } from "../../trainer/quality.ts";
import type { CoachCard, NextBookMove, OpponentNote, TrainMode } from "../../trainer/session.ts";
import { HINT_STEP_LABELS, type TutorHintStep } from "../../trainer/hints.ts";
import { partitionCoachAlternatives } from "../../lessons/assessment.ts";
import { bookSentence, engineSentence, planSentence, riskSentence } from "../../a11y/sentences.ts";
import type { BookAlternative } from "../../trainer/bookCoaching.ts";
import { readingFacts, type PositionReading } from "../../trainer/positionReading.ts";
import type { OpeningIntro } from "../../trainer/openingIntros.ts";
import type { ThemeStatus } from "../../trainer/themes.ts";
import { formatSampleLine } from "../../chess/notation.ts";
import type { CourseDeparture } from "../../trainer/courseRecovery.ts";
import { replySquares, type ReplyBriefing } from "../../trainer/replyBriefing.ts";

function PositionReadingPanel({
  reading,
  moveNumber,
  themes,
  revealNext,
}: {
  reading: PositionReading | null;
  moveNumber: number;
  themes: ThemeStatus[];
  /** Name the next course idea; From memory keeps it back. */
  revealNext: boolean;
}) {
  if (!reading || moveNumber < 5) return null;
  const facts = readingFacts(reading);
  const done = themes.filter((t) => t.state === "done").length;
  // The next idea is one whose course move is still ahead, not one whose
  // moment has passed in this game.
  const pending = themes.filter((t) => t.state === "pending" && t.mainlineMove >= moveNumber);
  return (
    <details className="position-reading" key={`reading-${moveNumber}`} open={moveNumber >= 10}>
      <summary>
        <span>Read the position</span>
        <span className="position-reading-character">
          {reading.character === "semi-open"
            ? reading.pawnsTraded
              ? "half-open center"
              : "no pawns traded yet"
            : `${reading.character} center`}
        </span>
      </summary>
      <dl className="rc-facts">
        {facts.map((fact) => (
          <div key={fact.label} className="rc-fact">
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
      <p>{reading.sentences.slice(2, 6).join(" ")}</p>
      {themes.length ? (
        <p className="theme-progress">
          {done} of {themes.length} course ideas practiced
          {revealNext && pending.length ? ` · next: ${pending[0].title}` : ""}
        </p>
      ) : null}
    </details>
  );
}

/**
 * The other moves in the position where the card's move was played, labelled
 * with that move number so they are not read as advice for the next move.
 */
function AlternativeGroups({
  alternatives,
  moveLabel,
}: {
  alternatives: CoachCard["alternatives"];
  moveLabel: string;
}) {
  const groups = partitionCoachAlternatives(alternatives);
  if (!groups.playable.length && !groups.sideline.length && !groups.avoid.length) return null;
  return (
    <div className="coach-alternatives">
      {groups.playable.length > 0 ? (
        <>
          <p className="alts-label">Also playable on {moveLabel}</p>
          <ul className="alts-list">
            {groups.playable.map((alt) => (
              <li key={`p-${alt.san}`}>
                <strong>{alt.san}</strong> — {alt.why}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {groups.sideline.length > 0 ? (
        <>
          <p className="alts-label">Known sideline on {moveLabel}</p>
          <ul className="alts-list">
            {groups.sideline.map((alt) => (
              <li key={`s-${alt.san}`}>
                <strong>{alt.san}</strong> — {alt.why}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {groups.avoid.length > 0 ? (
        <>
          <p className="alts-label">Avoid on {moveLabel}</p>
          <ul className="alts-list">
            {groups.avoid.map((alt) => (
              <li key={`a-${alt.san}`}>
                <strong>{alt.san}</strong> — {alt.why}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function MoveChoices({
  options,
  playerSide = "w",
}: {
  options: BookAlternative[];
  playerSide?: "w" | "b";
}) {
  if (!options.length) return null;
  const names = (
    <p className="coach-options">
      <strong>Acceptable alternatives.</strong>{" "}
      {options.map((option, index) => (
        <span key={option.san}>
          {index > 0 ? " · " : ""}
          <strong>{option.san}</strong> — {option.name}
        </span>
      ))}
      .
    </p>
  );
  if (!options.some((option) => option.line)) return names;
  return (
    <>
      {(options[0].moveNumber || 1) <= 2 ? names : null}
      <details
        className="coach-late-options"
        key={`${options[0].moveNumber}:${options.map((option) => option.san).join(",")}`}
      >
        <summary>
          Compare {options.length} acceptable{" "}
          {options.length === 1 ? "alternative" : "alternatives"}
        </summary>
        <p>
          These are playable choices in this position. Pick a plan you understand; the sample
          replies show one possible continuation.
        </p>
        <div className="coach-option-list">
          {options.map((option) => (
            <details className="coach-option" key={option.san}>
              <summary>
                <strong>{option.san}</strong> — {option.name}
              </summary>
              <p>
                <strong>The idea.</strong> {option.why}
              </p>
              {option.watch ? (
                <p>
                  <strong>The caution.</strong> {option.watch}
                </p>
              ) : null}
              {option.line ? (
                <p className="coach-sample-line">
                  <strong>One possible line.</strong>{" "}
                  {formatSampleLine(option.line, option.moveNumber || 10, playerSide)}
                </p>
              ) : null}
            </details>
          ))}
        </div>
      </details>
    </>
  );
}

/**
 * A yes/no the player sets once, kept in local storage so the panel opens the
 * way they left it. Storage can be missing or refused; the default then holds.
 */
function useStoredFlag(key: string, fallback = false): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored == null ? fallback : stored === "1";
    } catch {
      return fallback;
    }
  });
  const update = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, next ? "1" : "0");
      } catch {
        /* A private window or blocked storage keeps the choice for this page only. */
      }
    },
    [key]
  );
  return [value, update];
}

/**
 * The quiet chevron at the right of a section's eyebrow. It folds the
 * section to its first line for a player who wants only the next move in
 * view, and says what it does only to a screen reader or on hover.
 */
function SectionToggle({
  collapsed,
  label,
  onToggle,
}: {
  collapsed: boolean;
  label: string;
  onToggle: () => void;
}) {
  const verb = collapsed ? "Show" : "Hide";
  return (
    <button
      type="button"
      className="section-toggle"
      aria-expanded={!collapsed}
      aria-label={`${verb} ${label}`}
      title={`${verb} ${label}`}
      onClick={onToggle}
    >
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
        <path
          d="M4 6l4 4 4-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function joinLessonTitles(titles: string[]): string {
  if (titles.length <= 1) return titles[0] || "";
  if (titles.length === 2) return `${titles[0]} and the ${titles[1]}`;
  return `${titles.slice(0, -1).join(", the ")}, and the ${titles[titles.length - 1]}`;
}

export function CoachPanel({
  coach,
  inputLocked = false,
  intro = null,
  playerSide = "w",
  nextBook = null,
  moveNumber,
  objective,
  moveOptions,
  phaseLabel,
  mode,
  hints,
  hintRevealed,
  opponentNote,
  axesExpanded,
  onToggleAxes,
  onHint,
  positionReading = null,
  themes = [],
  onMainline = true,
  courseRecovery = null,
  courseTitle = "this course",
  onReturnToCourse,
  branchIntro = null,
  branchChoices = [],
  onChooseBranch,
  replyShownOnBoard = false,
  onShowReplyOnBoard,
  onReplyHover,
}: {
  coach: CoachCard | null;
  inputLocked?: boolean;
  intro?: OpeningIntro | null;
  playerSide?: "w" | "b";
  /** The course move to play now, in guided modes: shown before everything else. */
  nextBook?: NextBookMove | null;
  moveNumber: number;
  objective: string;
  moveOptions: BookAlternative[];
  phaseLabel: string;
  mode: TrainMode;
  hints: TutorHintStep[];
  hintRevealed: number;
  opponentNote: OpponentNote | null;
  axesExpanded: boolean;
  onToggleAxes: () => void;
  onHint: () => void;
  positionReading?: PositionReading | null;
  themes?: ThemeStatus[];
  /** The game is on the course's main line, where its ideas apply. */
  onMainline?: boolean;
  courseRecovery?: CourseDeparture | null;
  courseTitle?: string;
  onReturnToCourse?: () => void;
  /** Side lines: the named reply, and the other replies available here. */
  branchIntro?: string | null;
  branchChoices?: Array<{ san: string; name: string }>;
  onChooseBranch?: (san: string) => void;
  /** The reply card's squares are rung on the board. */
  replyShownOnBoard?: boolean;
  onShowReplyOnBoard?: (shown: boolean) => void;
  onReplyHover?: (hovering: boolean) => void;
}) {
  const judgment = coach ? QUALITY_BANDS[coach.judgment as QualityBandId] : null;
  const [verdictCollapsed, setVerdictCollapsed] = useStoredFlag("f15m_coach_fold_verdict");
  const [replyCollapsed, setReplyCollapsed] = useStoredFlag("f15m_coach_fold_reply");
  const introduction = (coach?.moveNumber ?? moveNumber) <= 2;
  const firstMove = coach?.moveNumber === 1;
  const choices = <MoveChoices options={moveOptions} playerSide={playerSide} />;
  const branchBlock =
    branchIntro || branchChoices.length > 1 ? (
      <div className="branch-intro">
        {branchIntro ? <p>{branchIntro}</p> : null}
        {branchChoices.length > 1 ? (
          <div className="branch-choices" role="group" aria-label="Other replies">
            {branchChoices.map((choice) => (
              <button
                key={choice.san}
                type="button"
                className="btn-ghost btn-sm"
                disabled={inputLocked}
                onClick={() => onChooseBranch?.(choice.san)}
              >
                {choice.san} · {choice.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    ) : null;
  // Main line and Side lines: the next course move, and why, is the first
  // thing read each turn. A beginner is told the move before being asked for it.
  const nextMove = nextBook ? (
    <section className="your-move next-book" aria-label="Your next move">
      <p className="ym-eyebrow">Your move</p>
      <h3 className="intro-move">
        Play {nextBook.moveNumber}
        {playerSide === "b" ? "…" : "."} {nextBook.san}
      </h3>
      {nextBook.inWords ? <p className="intro-move-words">{nextBook.inWords}</p> : null}
      <p className="intro-instruction">
        <strong>{nextBook.goal}</strong> {nextBook.why}
      </p>
      {nextBook.setsUp ? (
        <p className="intro-destination">
          <strong>Sets up.</strong> {nextBook.setsUp}
        </p>
      ) : null}
      {choices}
    </section>
  ) : null;
  const teaching = coach?.teaching ? (
    <div className={`coach-depth coach-depth-${coach.teaching.kind}`}>
      <p className="coach-depth-title">{coach.teaching.title}</p>
      {coach.teaching.depth ? (
        <>
          {coach.teaching.depth.strategy !== coach.reason ? (
            <p className="coach-strategy">
              <strong>Why it matters.</strong> {coach.teaching.depth.strategy}
            </p>
          ) : null}
          <details className="coach-strategy-details" key={coach.moveNumber ?? coach.san}>
            <summary>Go deeper: tradeoff</summary>
            <p>
              <strong>The tradeoff.</strong> {coach.teaching.depth.tradeoff}
            </p>
          </details>
          <p className="coach-takeaway">
            <strong>Remember.</strong> {coach.teaching.depth.takeaway}
          </p>
        </>
      ) : null}
      {!nextBook && coach.teaching.plan && coach.teaching.plan !== objective ? (
        <p>
          <strong>Build toward.</strong> {coach.teaching.plan}
        </p>
      ) : null}
      {!coach.teaching.watch ? null : introduction && !firstMove ? (
        <details className="coach-strategy-details" key={coach.moveNumber ?? coach.san}>
          <summary>What to watch for</summary>
          <p>{coach.teaching.watch}</p>
        </details>
      ) : (
        <p>
          <strong>Watch for.</strong> {coach.teaching.watch}
        </p>
      )}
    </div>
  ) : null;
  // Outside Main line the reply card would name moves still to come.
  const replyCard =
    practiceDisclosure(mode).futureMoves && opponentNote?.briefing ? (
      <ReplyCard
        briefing={opponentNote.briefing}
        title={opponentNote.title}
        moveNumber={opponentNote.moveNumber ?? moveNumber}
        mover={opponentNote.mover ?? (playerSide === "w" ? "b" : "w")}
        shownOnBoard={replyShownOnBoard}
        onShowOnBoard={onShowReplyOnBoard}
        onHover={onReplyHover}
        collapsed={replyCollapsed}
        onCollapse={setReplyCollapsed}
      />
    ) : null;

  return (
    <aside className="coach-panel trainer-coach" aria-label="Coach feedback">
      <div className="coach-header">
        <div>
          <p className="coach-name">Opening coach</p>
          {moveNumber > 0 ? (
            <p className="coach-status" id="phaseStage">
              {courseRecovery?.standing === "sound" ? "A different opening" : phaseLabel}
            </p>
          ) : null}
        </div>
      </div>

      {courseRecovery ? (
        <section className="your-move course-recovery" aria-label="Return to the course line">
          <p className="ym-eyebrow">
            {courseRecovery.standing === "sound"
              ? "Your move · a different opening route"
              : "Your move · off this lesson’s line"}
          </p>
          <h3>
            {courseRecovery.standing === "warned"
              ? `The course warns about ${courseRecovery.playedSan || "this move"}.`
              : courseRecovery.standing === "outside"
                ? `${courseRecovery.playedSan || "This move"} is outside this lesson’s book.`
                : courseRecovery.playedSan
                  ? `${courseRecovery.playedSan} is sound${
                      courseRecovery.routeName ? `: ${courseRecovery.routeName}` : ""
                    }.`
                  : `This move is sound.`}
          </h3>
          <p>
            {courseRecovery.standing === "warned"
              ? "The verdict below says why."
              : courseRecovery.standing === "outside"
                ? "That is not the same as wrong: the engine and the coach judge it on its merits."
                : `This ${courseTitle} lesson doesn’t teach that road.`}
            {courseRecovery.lessons?.length ? (
              <>
                {" "}
                The {joinLessonTitles(courseRecovery.lessons)}{" "}
                {courseRecovery.lessons.length === 1 ? "does" : "do"}.
              </>
            ) : (
              <> General coaching can stay with this position.</>
            )}{" "}
            To practice the {courseTitle} move, go back and play{" "}
            <strong>{courseRecovery.san}</strong>.
          </p>
          <button type="button" className="btn-ghost" onClick={onReturnToCourse}>
            Return to move {courseRecovery.moveNumber} · {courseRecovery.san}
          </button>
        </section>
      ) : null}
      {coach ? nextMove : null}
      {replyCard}
      {coach ? (
        <section
          className={`verdict compact-verdict${verdictCollapsed ? " is-folded" : ""}`}
          aria-label="Verdict on your last move"
        >
          <header className="verdict-head">
            <div className="fold-head">
              <p className="ym-eyebrow">Your last move</p>
              <SectionToggle
                collapsed={verdictCollapsed}
                label="the verdict on your last move"
                onToggle={() => setVerdictCollapsed(!verdictCollapsed)}
              />
            </div>
            <div className="verdict-meta">
              <span className="verdict-san">{coach.san}</span>
              <span className={`quality-band band-${coach.judgment}`}>
                {/* A book move's grade is the course's, not Stockfish's: the
                    session grades a sound course move as best practical
                    whatever its rank, so it never says "Stockfish" here. */}
                {(coach.bookStatus === "Book repertoire" || coach.bookStatus === "Transposition") &&
                coach.judgment === "best-practical"
                  ? "Book move"
                  : `Stockfish: ${
                      coach.judgment === "strong-alternative"
                        ? "Near its top choice"
                        : judgment?.label
                    }`}
              </span>
            </div>
          </header>
          {verdictCollapsed ? null : (
            <>
              <p className="verdict-effect">{coach.reason}</p>
              {coach.thread ? (
                <p className="coach-thread">
                  <strong>The thread.</strong> {coach.thread}
                </p>
              ) : null}
              {coach.teaching?.branches ? (
                <p className="coach-branches">
                  <strong>Other paths.</strong> {coach.teaching.branches}
                </p>
              ) : null}
              {!introduction ? teaching : null}
              {nextMove ? null : branchBlock}
              {nextMove ? null : (
                <p className="ym-primary">
                  <strong>Next decision.</strong> {objective}
                </p>
              )}
              {nextMove ? null : choices}
              {introduction && teaching ? (
                <details
                  className="coach-strategy-details coach-intro-details"
                  key={coach.moveNumber}
                >
                  <summary>More about this move</summary>
                  {teaching}
                </details>
              ) : null}
              <button
                className="hint-next"
                type="button"
                onClick={onToggleAxes}
                aria-expanded={axesExpanded}
              >
                {axesExpanded ? "Hide details" : "Book, engine, and alternatives"}
              </button>
              {axesExpanded ? (
                <div className="coach-axes">
                  <p>
                    <strong>Book.</strong> {bookSentence(coach.bookStatus, coach.bookDetail)}
                  </p>
                  <p>
                    <strong>Engine.</strong> {engineSentence(coach.engineBand)}
                    {coach.engineChoiceText ? ` ${coach.engineChoiceText}` : ""}
                  </p>
                  <p>
                    <strong>Plan.</strong> {planSentence(coach.planFit)}
                  </p>
                  <p>
                    <strong>Risk.</strong> {riskSentence(coach.tacticalStatus)}
                  </p>
                  <AlternativeGroups
                    alternatives={coach.alternatives}
                    moveLabel={`move ${coach.moveNumber}`}
                  />
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : inputLocked && !intro && !nextMove ? (
        <p role="status">Reviewing your move and preparing the reply…</p>
      ) : !intro && nextMove ? (
        nextMove
      ) : (
        <section className="your-move" aria-label="What to do now">
          <p className="ym-eyebrow">Your move</p>
          {intro ? (
            <>
              <h3 className="intro-move">
                Play 1{playerSide === "b" ? "…" : "."} {intro.san}
              </h3>
              <p className="intro-instruction">{intro.instruction}</p>
              <p className="intro-destination">{intro.destination}</p>
              {choices}
              <p className="intro-alternatives-note">{intro.alternativesNote}</p>
            </>
          ) : (
            <>
              {branchBlock}
              <p className="ym-primary">
                {mode === "review" ? "Play the move you learned in this position." : objective}
              </p>
              {choices}
            </>
          )}
        </section>
      )}

      <PositionReadingPanel
        reading={positionReading}
        moveNumber={coach?.moveNumber ?? moveNumber}
        themes={themes}
        revealNext={mode === "learn" && onMainline}
      />

      {hintRevealed > 0 ? (
        <div className="hint-ladder">
          <div className="hint-ladder-header">
            <span className="hint-ladder-label">Coaching hint</span>
            <button
              className="hint-next"
              type="button"
              onClick={onHint}
              disabled={hintRevealed >= 5}
            >
              {hintRevealed < 5 ? "Tell me more" : "All hints shown"}
            </button>
          </div>
          <ol className="hint-steps">
            {hints.slice(0, hintRevealed).map((step) => (
              <li key={step.id}>
                <span className="hint-step-label">{HINT_STEP_LABELS[step.id]}</span>
                <RichText text={step.text} />
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </aside>
  );
}

/** The replies the opponent did not play, inline: "…c5 (Sicilian), …e6 (French)". */
function notPlayed(alts: ReplyBriefing["turnedDown"], mover: "w" | "b"): string {
  const named = (alts || []).filter((alt) => alt.name);
  if (!named.length) return "";
  const dots = mover === "b" ? "…" : "";
  const parts = named.map((alt) => `${dots}${alt.san} (${alt.name})`);
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}`;
}

/**
 * The card on the move the opponent just played. Two tiers: the idea, the
 * hook and your chance stay in view; what it does, their plan, its cost and
 * Stockfish's view wait behind one disclosure, closed for each new move.
 */
function ReplyCard({
  briefing,
  title,
  moveNumber,
  mover,
  shownOnBoard,
  onShowOnBoard,
  onHover,
  collapsed,
  onCollapse,
}: {
  briefing: ReplyBriefing;
  title: string;
  moveNumber: number;
  mover: "w" | "b";
  /** The squares the card names are rung on the board. */
  shownOnBoard: boolean;
  onShowOnBoard?: (shown: boolean) => void;
  onHover?: (hovering: boolean) => void;
  /** Folded to its first line, the move and its name. */
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}) {
  const eyebrow = mover === "b" ? "Black’s reply" : "White’s reply";
  const alternatives = moveNumber <= 4 ? notPlayed(briefing.turnedDown, mover) : "";
  const more: Array<[string, string | undefined]> = [
    ["What it does", briefing.onTheBoard],
    ["Their plan", briefing.theyWant],
    ["What it cost", briefing.cost],
    ["Stockfish", briefing.soundness],
    ["Opening", briefing.opening],
  ];
  const details = more.filter(([, text]) => text);
  const canShow = Boolean(onShowOnBoard) && replySquares(briefing).length > 0;
  return (
    <section
      className={`opponent-notes reply-briefing${collapsed ? " is-folded" : ""}`}
      id="opponentNotes"
      aria-label={eyebrow}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <div className="fold-head">
        <p className="ym-eyebrow">{eyebrow}</p>
        <SectionToggle
          collapsed={collapsed}
          label={`${eyebrow.toLowerCase()} note`}
          onToggle={() => onCollapse(!collapsed)}
        />
      </div>
      <h3 className="reply-briefing-title">{title}</h3>
      {collapsed ? null : (
        <div className="opponent-notes-body" id="opponentNotesBody">
          <p className="reply-idea">{briefing.idea}</p>
          {briefing.theyHope ? (
            <p>
              <strong>They hope you miss.</strong> {briefing.theyHope}
            </p>
          ) : null}
          {briefing.watch ? (
            <p className="reply-watch">
              <strong>Your chance.</strong> {briefing.watch}
            </p>
          ) : null}
          {alternatives ? (
            <p className="reply-not-played">
              <strong>Not played.</strong> {alternatives}.
            </p>
          ) : null}
          {!briefing.prepared ? (
            <p className="reply-reading">
              Read from the board. The course has no lesson for this move.
            </p>
          ) : null}
          {details.length || canShow ? (
            // The disclosure and the board button share one row; the opened
            // disclosure grows below while the button keeps its place.
            <div className="reply-foot">
              {details.length ? (
                <details className="coach-strategy-details reply-more" key={title}>
                  <summary>Why they played it</summary>
                  {details.map(([label, text]) => (
                    <p key={label}>
                      <strong>{label}.</strong> {text}
                    </p>
                  ))}
                </details>
              ) : (
                <span />
              )}
              {canShow ? (
                <button
                  type="button"
                  className="btn-ghost btn-sm reply-show-board"
                  aria-pressed={shownOnBoard}
                  onClick={() => onShowOnBoard?.(!shownOnBoard)}
                >
                  {shownOnBoard ? "Shown on board" : "Show on board"}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
